import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Box,
    Typography,
    IconButton,
} from "@mui/material";
import { toast } from "react-toastify";
import { Delete } from "@mui/icons-material";
import apiClient from "../utils/apiClient";
import FallbackImage from "../components/FallbackImage";
import { getImageFullUrl } from "../utils/common";


const BannerDialog = ({ open, onClose, editData, onSaved }) => {
    const [formData, setFormData] = useState({
        id: "",
        images: null,
        imagePreview: "",
        status: "active",
        catId: "",
        subCatId: "",
        thirdsubCatId: "",
        price: '',
        bannerTitle: '',
        alignInfo: '',
    });
    const [loading, setLoading] = useState(false);

    const [categories, setCategories] = useState([]);
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === "checkbox") {
            setFormData({ ...formData, [name]: checked });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    // ✅ Load categories from backend
    const fetchCategories = async () => {
        try {
            const res = await apiClient.get("/api/categories");
            setCategories(res?.data?.data || []);
        } catch (err) {
            toast.error("Failed to load categories");
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    // ✅ Prefill data when editing
    useEffect(() => {
        if (editData) {
            setFormData({
                id: editData.id,
                images: null,
                imagePreview: editData.images ? getImageFullUrl(editData.images) : "",
                status: editData.status,
                catId: editData.catId || "",
                subCatId: editData.subCatId || "",
                thirdsubCatId: editData.thirdsubCatId || "",
                price: editData.price || '',
                bannerTitle: editData.bannerTitle || '',
                alignInfo: editData.alignInfo || '',
            });
        } else {
            setFormData({
                id: "",
                images: null,
                imagePreview: "",
                status: "active",
                catId: "",
                subCatId: "",
                thirdsubCatId: "",
                price: '',
                bannerTitle: '',
                alignInfo: '',
            });
        }
    }, [editData]);

    // ✅ Handle file upload
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
            toast.error("Only JPG, PNG, or WEBP allowed");
            return;
        }
        setFormData({
            ...formData,
            images: file,
            imagePreview: URL.createObjectURL(file),
        });
    };

    // ✅ Save banner
    const handleSave = async () => {
        if (!formData.bannerTitle) {
            toast.error("Banner Title is required");
            return;
        }
        if (!formData.alignInfo) {
            toast.error("AlignInfo is required");
            return;
        }
        if (!formData.price) {
            toast.error("Price is required");
            return;
        }
        if (!formData.catId) {
            toast.error("Category is required");
            return;
        }
        if (!formData.images && !formData.imagePreview) {
            toast.error("Please upload a banner image");
            return;
        }
        setLoading(true)
        try {
            const data = new FormData();
            if (formData.images) data.append("image", formData.images);
            data.append("status", formData.status);
            data.append("catId", formData.catId);
            data.append("subCatId", formData.subCatId);
            data.append("thirdsubCatId", formData.thirdsubCatId);
            data.append("price", formData.price);
            data.append("bannerTitle", formData.bannerTitle);
            data.append("alignInfo", formData.alignInfo);

            if (formData.id) {
                await apiClient.put(`/api/banner/${formData.id}`, data, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                toast.success("Banner updated");
            } else {
                await apiClient.post(`/api/banner`, data, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                toast.success("Banner created");
            }

            onSaved(); // refresh list
            onClose();
        } catch (err) {
            toast.error("Failed to save banner");
        }finally{
            setLoading(false)
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle>
                {formData.id ? "Edit Banner" : "Add Banner"}
            </DialogTitle>
            <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Category Dropdown */}
                <TextField
                    fullWidth
                    label="Banner Title"
                    name="bannerTitle"
                    value={formData.bannerTitle}
                    onChange={handleChange}
                    error={!!errors.bannerTitle}
                    helperText={errors.bannerTitle}
                />
                <TextField label="Select AlignInfo" name="alignInfo"
                    select value={formData.alignInfo} onChange={handleChange}>
                    <MenuItem value="left">Left</MenuItem>
                    <MenuItem value="right">Right</MenuItem>
                </TextField>
                <TextField
                    fullWidth
                    label="Price"
                    name="price"
                    inputProps={{
                        min: 0,
                    }}
                    type="number"
                    value={formData.price}
                    onChange={handleChange}
                    error={!!errors.price}
                    helperText={errors.price}
                />
                <TextField
                    select
                    label="Select Category"
                    value={formData.catId}
                    onChange={(e) => setFormData({ ...formData, catId: e.target.value, subCatId: "", thirdsubCatId: "" })}
                    fullWidth
                >
                    {categories.map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>
                            {cat.name}
                        </MenuItem>
                    ))}
                </TextField>

                {/* Sub Category Dropdown */}
                {formData.catId && (
                    <TextField
                        select
                        label="Select Sub Category"
                        value={formData.subCatId}
                        onChange={(e) => setFormData({ ...formData, subCatId: e.target.value, thirdsubCatId: "" })}
                        fullWidth
                    ><MenuItem value={""}>
                            Select Sub Category
                        </MenuItem>
                        {categories
                            .find((c) => c.id === formData.catId)?.subcategories?.map((sub) => (
                                <MenuItem key={sub.id} value={sub.id}>
                                    {sub.name}
                                </MenuItem>
                            ))}
                    </TextField>
                )}

                {/* Third Sub Category Dropdown */}
                {formData.subCatId && (
                    <TextField
                        select
                        label="Select Third Sub Category"
                        value={formData.thirdsubCatId}
                        onChange={(e) => setFormData({ ...formData, thirdsubCatId: e.target.value })}
                        fullWidth
                    >
                        <MenuItem value={""}>
                            Select Third Sub Category
                        </MenuItem>
                        {categories
                            .find((c) => c.id === formData.catId)
                            ?.subcategories.find((s) => s.id === formData.subCatId)
                            ?.subcategories?.map((third) => (
                                <MenuItem key={third.id} value={third.id}>
                                    {third.name}
                                </MenuItem>
                            ))}
                    </TextField>
                )}

                {/* Status */}
                <TextField
                    select
                    label="Status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    fullWidth
                >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>

                {/* Upload Image */}
                <Box>
                    <Button variant="outlined" component="label">
                        Upload Banner
                        <input hidden type="file" accept="image/*" onChange={handleFileChange} />
                    </Button>

                    {formData.imagePreview && (
                        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
                            <FallbackImage
                                src={formData.imagePreview}
                                alt="banner-preview"
                                style={{ width: "120px", borderRadius: "8px" }}
                            />
                            <IconButton onClick={() => setFormData({ ...formData, images: null, imagePreview: "" })}>
                                <Delete />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button disabled={loading} variant="contained" onClick={handleSave}>
                    {formData.id ? "Update" : "Create"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BannerDialog;
